/**
 * Prompt 3: recipient-centric send matrix for the Approve & Send modal.
 *
 * One card per RECIPIENT (not per document). Each card shows the address,
 * why it was chosen (tier badge), and a row of document chips — Results /
 * Invoice / Estimate / Photos. Recipients ride ONE shared Cc thread by default
 * (reply-all keeps one conversation); "send separately" pulls a recipient onto
 * its own email. The summary line below the cards states the exact email count.
 *
 * The office can remove a recipient, add a saved contact method, or add a
 * one-time address (never persisted). "Remember routing" on a saved method
 * writes contact_method_purposes so the same chips pre-check next send.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Loader2, Mail, Plus, X, UserPlus } from "lucide-react";
import { UndeliverableBadge } from "@/components/undeliverable-badge";

export type RecipientSource = "resolved" | "saved" | "one_time";

export type PacketDoc = "report" | "invoice" | "estimate" | "photos";

export interface PlanRecipient {
    value: string;
    source: RecipientSource;
    contactMethodId: string | null;
    contactName: string | null;
    tier: string | null;
    docs: PacketDoc[];
    remember: boolean;
    /** Per-send Cc opt-out: send this address its own email instead of riding
     *  the shared Cc thread. Default false = grouped. */
    sendSeparately: boolean;
}

interface PreviewMatrixRecipient {
    value: string;
    contactName: string | null;
    contactMethodId: string | null;
    tier: string | null;
    docs: string[];
}

interface PreviewDocument {
    documentType: "invoice" | "estimate" | "report";
    exists: boolean;
    count: number;
}

interface AvailableRecipient {
    value: string;
    contactName: string | null;
    contactMethodId: string | null;
    isBilling: boolean;
    isPrimary: boolean;
    /** Money-safe default chips: reports→everyone, bills→billing/primary. */
    defaultDocs: string[];
}

/** A recipient who missed the last send (failed delivery or dropped) — shaped
 *  like a plan row so the editor can seed a resend straight from it. */
interface MissedRecipient {
    value: string;
    contactName: string | null;
    contactMethodId: string | null;
    docs: string[];
    reason: "failed" | "dropped";
}

interface SendPreview {
    customerId: string | null;
    siteId: string | null;
    hasPhotos: boolean;
    photoCount: number;
    portalJob?: boolean;
    documents: PreviewDocument[];
    recipients: PreviewMatrixRecipient[];
    /** Every contact on file, each with its money-safe default chips. */
    availableRecipients?: AvailableRecipient[];
    /** Distinct email-contact count on file (drives the "not receiving" nudge). */
    contactsTotal?: number;
    /** Recipients who missed the job's last send — powers "Resend to who missed it". */
    missedRecipients?: MissedRecipient[];
}

interface ContactMethod {
    id: string;
    kind: string;
    value: string;
    label: string | null;
    memo: string | null;
    fromServiceTitan: boolean;
    stSortOrder: number | null;
    isPrimary: boolean;
    purposes: string[];
    undeliverableAt?: string | null;
    undeliverableReason?: string | null;
}

interface ContactWithMethods {
    id: string;
    firstName: string | null;
    lastName: string | null;
    roleTitle: string | null;
    siteId: string | null;
    isPrimary: boolean;
    isBilling: boolean;
    methods: ContactMethod[];
}

const TIER_LABEL: Record<string, string> = {
    routing_rule: "Saved routing",
    location_purpose: "Location purpose",
    account_purpose: "Account purpose",
    primary: "Primary",
    legacy: "Legacy",
};

const TIER_TOOLTIP: Record<string, string> = {
    routing_rule:
        "Saved routing rule — this address is set to receive these documents (remembered from a previous send or set on the customer's Contacts & Routing tab).",
    location_purpose:
        "Saved routing for this location — this address is set to always receive these documents for this site.",
    account_purpose: "Saved routing for this customer account — applies to every location.",
    primary: "Primary contact for the customer — receives all documents by default.",
    legacy: "Unverified address carried over from the legacy customer record. Confirm it's correct before relying on it.",
};

function tierBadgeClass(tier: string | null | undefined): string {
    switch (tier) {
        case "routing_rule":
        case "location_purpose":
            return "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200";
        case "account_purpose":
            return "border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200";
        case "primary":
            return "border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-200";
        case "legacy":
            // Amber so the office immediately sees this address is unverified.
            return "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200";
        default:
            return "border-slate-300 bg-slate-100 text-slate-600";
    }
}

export const DOC_LABEL: Record<PacketDoc, string> = {
    report: "Results",
    invoice: "Invoice",
    estimate: "Estimate",
    photos: "Photos",
};

const DOC_ORDER: PacketDoc[] = ["report", "invoice", "estimate", "photos"];

/** Purpose keys → the chips they pre-check when adding a saved method. */
const PURPOSE_TO_DOCS: Record<string, PacketDoc[]> = {
    send_invoices: ["invoice"],
    send_quotes: ["estimate"],
    send_reports: ["report", "photos"],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function contactDisplayName(c: ContactWithMethods): string {
    const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
    return name || "Unnamed contact";
}

/** Human summary of a chip set: "results + invoice + photos". */
export function docsSummary(docs: PacketDoc[]): string {
    return DOC_ORDER.filter((d) => docs.includes(d))
        .map((d) => DOC_LABEL[d].toLowerCase())
        .join(" + ");
}

/**
 * #833 (office audit 4, C5) — ONE truth for "how many emails will this send
 * fire, and in what mode". The audit caught the recipients panel saying
 * "1 email — 1 shared thread" while the send caption beside the button said
 * "3 emails — one per recipient" on the same Cintas job: the caption was
 * counting plan rows while the panel mirrored the server's Cc grouping
 * (groupRecipientsForCc: everyone not opted out rides ONE email; opted-out
 * addresses each get their own). Every surface that states the count or the
 * mode must read this summary — the mode decides whether three contacts at a
 * national account see each other's addresses and the invoice.
 */
export interface SendPlanSummary {
    emailCount: number;
    /** Recipients riding the shared Cc thread (a "thread" of 1 is just their own email). */
    sharedCount: number;
    separateCount: number;
    /** The mode in words — the fact the privacy warning hangs on. */
    modeLabel: string;
}

export function summarizeSendPlan(plan: ReadonlyArray<Pick<PlanRecipient, "sendSeparately">>): SendPlanSummary {
    const sharedCount = plan.filter((r) => !r.sendSeparately).length;
    const separateCount = plan.length - sharedCount;
    const emailCount = (sharedCount > 0 ? 1 : 0) + separateCount;
    let modeLabel: string;
    if (plan.length === 0) {
        modeLabel = "no recipients yet";
    } else if (sharedCount >= 2 && separateCount === 0) {
        modeLabel = `one shared email, all ${sharedCount} recipients CC'd together`;
    } else if (sharedCount >= 2) {
        modeLabel = `one shared email (${sharedCount} CC'd together) + ${separateCount} separate email${separateCount === 1 ? "" : "s"}`;
    } else if (emailCount === 1) {
        modeLabel = "one email to one recipient";
    } else {
        modeLabel = `${emailCount} separate emails, one per recipient`;
    }
    return { emailCount, sharedCount, separateCount, modeLabel };
}

export function SendRecipientEditor({
    jobId,
    customerId,
    include,
    onChange,
    onMissingDocsChange,
    onlyMissed = false,
    onMissedCountChange,
}: {
    jobId: string;
    customerId: string | null;
    /** Which document types are currently checked for sending. */
    include: { invoices: boolean; estimates: boolean; reports: boolean; photos: boolean };
    onChange: (plan: PlanRecipient[]) => void;
    /**
     * Emits the list of in-play documents that currently have NO recipient
     * chip anywhere — the parent disables Send until this is empty, so a
     * document can never be silently dropped from a send.
     */
    onMissingDocsChange?: (missing: PacketDoc[]) => void;
    /** Resend mode: seed ONLY the recipients who missed the last send, not the
     *  full account. Toggling it re-seeds the matrix. */
    onlyMissed?: boolean;
    /** Emits how many recipients missed the last send, so the parent can offer
     *  the one-click "Resend to who missed it". */
    onMissedCountChange?: (count: number) => void;
}) {
    const preview = useQuery({
        queryKey: ["send-preview", jobId],
        queryFn: () => api.get<SendPreview>(`/api/office-review/jobs/${jobId}/send-preview`),
        enabled: !!jobId,
    });

    const contactsQuery = useQuery({
        queryKey: ["customer-contact-methods", customerId],
        queryFn: () => api.get<{ contacts: ContactWithMethods[] }>(`/api/customers/${customerId}/contact-methods`),
        enabled: !!customerId,
    });

    // Addresses SendGrid has told us don't take mail (migration 626), so the
    // office sees "Bounced" before sending to one again.
    const undeliverable = useMemo(() => {
        const m = new Map<string, { at: string; reason: string | null }>();
        for (const c of contactsQuery.data?.contacts ?? [])
            for (const x of c.methods)
                if (x.kind === "email" && x.undeliverableAt)
                    m.set(x.value.trim().toLowerCase(), {
                        at: x.undeliverableAt,
                        reason: x.undeliverableReason ?? null,
                    });
        return m;
    }, [contactsQuery.data]);

    const [plan, setPlan] = useState<PlanRecipient[]>([]);
    const [seeded, setSeeded] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [showOneTime, setShowOneTime] = useState(false);
    const [oneTimeValue, setOneTimeValue] = useState("");

    // Seed the matrix from the preview once it loads. Two sources merged by
    // email so nobody on the account is silently omitted:
    //   1. resolved recipients (routing/purpose/primary) — pre-checked with the
    //      chips the server resolved for them (unchanged behavior).
    //   2. every OTHER contact on file — pre-checked with money-safe default
    //      chips (reports→everyone, bills→billing/primary) so the default
    //      reaches the whole account, not just the first-tier address.
    // Re-seed whenever the mode flips (default ⇄ resend-to-missed).
    useEffect(() => {
        setSeeded(false);
    }, [onlyMissed]);

    // Tell the parent how many missed the last send (drives the resend button).
    useEffect(() => {
        onMissedCountChange?.(preview.data?.missedRecipients?.length ?? 0);
    }, [preview.data, onMissedCountChange]);

    useEffect(() => {
        if (!preview.data || seeded) return;
        // Resend mode: seed ONLY the recipients who missed the last send, each
        // with the docs they were meant to get — one click, no re-blast.
        if (onlyMissed) {
            const missed: PlanRecipient[] = (preview.data.missedRecipients ?? [])
                .map((m) => ({
                    value: m.value,
                    source: (m.contactMethodId ? "saved" : "one_time") as RecipientSource,
                    contactMethodId: m.contactMethodId,
                    contactName: m.contactName,
                    tier: null,
                    docs: m.docs.filter((d): d is PacketDoc => DOC_ORDER.includes(d as PacketDoc)),
                    remember: false,
                    sendSeparately: false,
                }))
                .filter((r) => r.docs.length > 0);
            setPlan(missed);
            setSeeded(true);
            return;
        }
        const resolved: PlanRecipient[] = (preview.data.recipients ?? []).map((r) => ({
            value: r.value,
            source: "resolved" as RecipientSource,
            contactMethodId: r.contactMethodId,
            contactName: r.contactName,
            tier: r.tier,
            docs: r.docs.filter((d): d is PacketDoc => DOC_ORDER.includes(d as PacketDoc)),
            remember: false,
            sendSeparately: false,
        }));
        const present = new Set(resolved.map((r) => r.value.toLowerCase()));
        const extras: PlanRecipient[] = (preview.data.availableRecipients ?? [])
            .filter((a) => !present.has(a.value.toLowerCase()))
            .map((a) => ({
                value: a.value,
                source: "saved" as RecipientSource,
                contactMethodId: a.contactMethodId,
                contactName: a.contactName,
                tier: null,
                docs: a.defaultDocs.filter((d): d is PacketDoc => DOC_ORDER.includes(d as PacketDoc)),
                remember: false,
                sendSeparately: false,
            }))
            // Only card contacts that have a default document; the rest stay in
            // the "Add saved contact" panel and are surfaced by the nudge below.
            .filter((r) => r.docs.length > 0);
        setPlan([...resolved, ...extras]);
        setSeeded(true);
    }, [preview.data, seeded, onlyMissed]);

    // Which docs are in play: checkbox on AND the doc exists on the job.
    const docExists = useMemo(() => {
        const map: Record<PacketDoc, boolean> = { report: false, invoice: false, estimate: false, photos: false };
        for (const doc of preview.data?.documents ?? []) {
            if (doc.documentType === "report") map.report = doc.exists;
            if (doc.documentType === "invoice") map.invoice = doc.exists;
            if (doc.documentType === "estimate") map.estimate = doc.exists;
        }
        map.photos = (preview.data?.photoCount ?? 0) > 0;
        return map;
    }, [preview.data]);

    const inPlayDocs: PacketDoc[] = DOC_ORDER.filter((d) => {
        const flagOn =
            d === "invoice"
                ? include.invoices
                : d === "estimate"
                  ? include.estimates
                  : d === "report"
                    ? include.reports
                    : include.photos;
        return flagOn && docExists[d];
    });

    // Effective plan: chips filtered to in-play docs, empty cards excluded.
    const effectivePlan = useMemo(
        () =>
            plan
                .map((r) => ({ ...r, docs: r.docs.filter((d) => inPlayDocs.includes(d)) }))
                .filter((r) => r.docs.length > 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [plan, inPlayDocs.join(",")],
    );

    // Mirror up on every change.
    useEffect(() => {
        onChange(effectivePlan);
    }, [effectivePlan, onChange]);

    // Shared-thread (Cc) grouping mirror — display only, mirrors the server's
    // groupRecipientsForCc: everyone not opted out rides ONE email; opted-out
    // addresses each get their own. Drives the "Shared thread" indicator and the
    // truthful email count below.
    const ccShared = useMemo(() => effectivePlan.filter((r) => !r.sendSeparately), [effectivePlan]);
    const ccSeparate = useMemo(() => effectivePlan.filter((r) => r.sendSeparately), [effectivePlan]);
    const sharedThreadCount = ccShared.length;
    // #833: counts come from the ONE summary every send surface reads.
    const { emailCount } = summarizeSendPlan(effectivePlan);
    const sharedThreadValues = useMemo(() => new Set(ccShared.map((r) => r.value.toLowerCase())), [ccShared]);
    const onSharedThread = (value: string) => sharedThreadCount > 1 && sharedThreadValues.has(value.toLowerCase());
    // The shared email carries the UNION of its members' docs (everyone on the
    // thread gets the whole packet).
    const sharedDocs = useMemo(() => {
        const s = new Set<PacketDoc>();
        for (const r of ccShared) for (const d of r.docs) s.add(d);
        return DOC_ORDER.filter((d) => s.has(d));
    }, [ccShared]);

    // Validation: every in-play doc needs at least one chip somewhere.
    const missingDocs = inPlayDocs.filter((d) => !effectivePlan.some((r) => r.docs.includes(d)));

    // Nudge: contacts on file that currently aren't receiving anything. A
    // money-doc-only send defaults bills to billing/primary, so the rest of the
    // account won't be carded — surface them so the office can add anyone they
    // want. Purely informational (added via the "Add saved contact" panel).
    const receivingEmails = new Set(effectivePlan.map((r) => r.value.toLowerCase()));
    const onFileNotReceiving = (preview.data?.availableRecipients ?? []).filter(
        (a) => !receivingEmails.has(a.value.toLowerCase()),
    );
    useEffect(() => {
        onMissingDocsChange?.(preview.isLoading ? [] : missingDocs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingDocs.join(","), preview.isLoading, onMissingDocsChange]);

    if (preview.isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-foreground/60 border-t pt-3">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Resolving recipients…
            </div>
        );
    }

    if (inPlayDocs.length === 0) {
        return null;
    }

    const addRecipient = (recip: PlanRecipient) => {
        setPlan((prev) => {
            const exists = prev.some((r) => r.value.toLowerCase() === recip.value.toLowerCase());
            if (exists) return prev;
            return [...prev, recip];
        });
        setShowSaved(false);
        setShowOneTime(false);
    };

    const removeRecipient = (value: string) => {
        setPlan((prev) => prev.filter((r) => r.value.toLowerCase() !== value.toLowerCase()));
    };

    const toggleDoc = (value: string, doc: PacketDoc) => {
        setPlan((prev) =>
            prev.map((r) =>
                r.value.toLowerCase() === value.toLowerCase()
                    ? { ...r, docs: r.docs.includes(doc) ? r.docs.filter((d) => d !== doc) : [...r.docs, doc] }
                    : r,
            ),
        );
    };

    const toggleRemember = (value: string) => {
        setPlan((prev) =>
            prev.map((r) => (r.value.toLowerCase() === value.toLowerCase() ? { ...r, remember: !r.remember } : r)),
        );
    };

    const toggleSeparate = (value: string) => {
        setPlan((prev) =>
            prev.map((r) =>
                r.value.toLowerCase() === value.toLowerCase() ? { ...r, sendSeparately: !r.sendSeparately } : r,
            ),
        );
    };

    const submitOneTime = () => {
        const v = oneTimeValue.trim();
        if (!EMAIL_RE.test(v)) return;
        addRecipient({
            value: v,
            source: "one_time",
            contactMethodId: null,
            contactName: null,
            tier: null,
            docs: [...inPlayDocs],
            remember: false,
            sendSeparately: false,
        });
        setOneTimeValue("");
    };

    const alreadyAdded = new Set(plan.map((r) => r.value.toLowerCase()));
    const emailContacts: ContactWithMethods[] = (contactsQuery.data?.contacts ?? [])
        .map((c) => ({ ...c, methods: c.methods.filter((m) => m.kind === "email") }))
        .filter((c) => c.methods.length > 0);

    return (
        <div className="border-t pt-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-3.5 w-3.5 text-foreground/80" />
                Recipients
            </div>
            <p className="text-xs text-foreground/60">
                Each recipient gets exactly one email containing the documents ticked on their card. One-time addresses
                are used only for this send and never saved.
            </p>

            {effectivePlan.length === 0 && plan.length === 0 ? (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                    No recipients resolved for this customer — add a saved contact or one-time address below.
                </p>
            ) : null}

            <ul className="space-y-2">
                {plan.map((r) => {
                    const cardDocs = r.docs.filter((d) => inPlayDocs.includes(d));
                    return (
                        <li key={r.value} className="rounded-md border border-border/60 p-2.5 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                                <span className="break-all font-medium">{r.value}</span>
                                {undeliverable.get(r.value.trim().toLowerCase()) ? (
                                    <UndeliverableBadge
                                        at={undeliverable.get(r.value.trim().toLowerCase())!.at}
                                        reason={undeliverable.get(r.value.trim().toLowerCase())!.reason}
                                    />
                                ) : null}
                                {r.contactName ? (
                                    <span className="text-xs text-foreground/60 whitespace-nowrap">
                                        ({r.contactName})
                                    </span>
                                ) : null}
                                {r.source === "one_time" ? (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px] border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                                        title="Used only for this send — never saved to the customer record."
                                    >
                                        One-time
                                    </Badge>
                                ) : r.source === "saved" ? (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px]"
                                        title="Added from the customer's saved contacts."
                                    >
                                        Added
                                    </Badge>
                                ) : (
                                    <Badge
                                        variant="outline"
                                        className={cn("text-[10px]", tierBadgeClass(r.tier))}
                                        title={
                                            TIER_TOOLTIP[r.tier ?? ""] ??
                                            "Resolved from the customer's contact hierarchy."
                                        }
                                    >
                                        {TIER_LABEL[r.tier ?? ""] ?? r.tier ?? "Resolved"}
                                    </Badge>
                                )}
                                {r.tier === "legacy" ? (
                                    <span
                                        className="text-[10px] text-amber-700 dark:text-amber-300 whitespace-nowrap"
                                        title={TIER_TOOLTIP.legacy}
                                    >
                                        unverified
                                    </span>
                                ) : null}
                                {onSharedThread(r.value) ? (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px] border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200"
                                        title="CC'd on one shared email with the other recipients — a reply-all keeps everyone on the thread."
                                    >
                                        Shared thread
                                    </Badge>
                                ) : r.sendSeparately && effectivePlan.length > 1 ? (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px]"
                                        title="Sent as its own individual email, off the shared thread."
                                    >
                                        Separate
                                    </Badge>
                                ) : null}
                                <button
                                    type="button"
                                    className="ml-auto text-muted-foreground hover:text-red-600"
                                    onClick={() => removeRecipient(r.value)}
                                    aria-label={`Remove ${r.value}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {inPlayDocs.map((d) => {
                                    const on = cardDocs.includes(d);
                                    return (
                                        <button
                                            key={d}
                                            type="button"
                                            className={cn(
                                                "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                                                on
                                                    ? "border-green-600 bg-green-600/10 text-green-700 dark:text-green-300 font-medium"
                                                    : "border-border/60 text-foreground/50 hover:text-foreground/80",
                                            )}
                                            aria-pressed={on}
                                            onClick={() => toggleDoc(r.value, d)}
                                        >
                                            {on ? "✓ " : ""}
                                            {DOC_LABEL[d]}
                                        </button>
                                    );
                                })}
                            </div>
                            {cardDocs.length === 0 ? (
                                <p className="text-[11px] text-muted-foreground">
                                    No documents selected — this recipient will not receive an email.
                                </p>
                            ) : null}
                            {r.contactMethodId ? (
                                <label className="flex items-center gap-1.5 text-[11px] text-foreground/60">
                                    <input
                                        type="checkbox"
                                        checked={r.remember}
                                        onChange={() => toggleRemember(r.value)}
                                        className="rounded border-input h-3 w-3"
                                    />
                                    Always send these documents to this contact (remember routing)
                                </label>
                            ) : null}
                            {effectivePlan.length > 1 && cardDocs.length > 0 ? (
                                <label className="flex items-center gap-1.5 text-[11px] text-foreground/60">
                                    <input
                                        type="checkbox"
                                        checked={r.sendSeparately}
                                        onChange={() => toggleSeparate(r.value)}
                                        className="rounded border-input h-3 w-3"
                                    />
                                    Send as a separate email (don&apos;t CC on the shared thread)
                                </label>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
            {sharedThreadCount > 1 ? (
                <p className="text-[11px] text-foreground/55">
                    The {sharedThreadCount} recipients on the shared thread are CC&apos;d on{" "}
                    <span className="font-medium">one email</span> — they&apos;ll see each other&apos;s addresses (and
                    the invoice). Tick &ldquo;send separately&rdquo; on anyone who should get their own email.
                </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    onClick={() => {
                        setShowSaved((v) => !v);
                        setShowOneTime(false);
                    }}
                >
                    <UserPlus className="h-3 w-3" /> Add saved contact
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    onClick={() => {
                        setShowOneTime((v) => !v);
                        setShowSaved(false);
                    }}
                >
                    <Plus className="h-3 w-3" /> Add one-time
                </Button>
            </div>

            {showSaved ? (
                <div className="rounded border border-border/60 p-2 space-y-1.5 max-h-48 overflow-y-auto">
                    {contactsQuery.isLoading ? (
                        <div className="flex items-center gap-2 text-xs text-foreground/60">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading contacts…
                        </div>
                    ) : emailContacts.length === 0 ? (
                        <p className="text-xs text-foreground/60">No saved email contacts for this customer.</p>
                    ) : (
                        emailContacts.map((c) => (
                            <div key={c.id} className="space-y-1">
                                <div className="text-xs font-medium text-foreground/80">
                                    {contactDisplayName(c)}
                                    {c.roleTitle ? (
                                        <span className="text-muted-foreground"> · {c.roleTitle}</span>
                                    ) : null}
                                    {c.siteId ? <span className="text-muted-foreground"> · location</span> : null}
                                </div>
                                {c.methods.map((m) => {
                                    const added = alreadyAdded.has(m.value.toLowerCase());
                                    // Saved purposes pre-check their chips; a
                                    // method with no purposes defaults to all
                                    // in-play docs.
                                    const purposeDocs = [
                                        ...new Set(m.purposes.flatMap((p) => PURPOSE_TO_DOCS[p] ?? [])),
                                    ].filter((d) => inPlayDocs.includes(d));
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            disabled={added}
                                            className={cn(
                                                "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm",
                                                added
                                                    ? "text-muted-foreground cursor-not-allowed"
                                                    : "hover:bg-muted/60",
                                            )}
                                            onClick={() => {
                                                addRecipient({
                                                    value: m.value,
                                                    source: "saved",
                                                    contactMethodId: m.id,
                                                    contactName: contactDisplayName(c),
                                                    tier: null,
                                                    docs: purposeDocs.length > 0 ? purposeDocs : [...inPlayDocs],
                                                    remember: false,
                                                    sendSeparately: false,
                                                });
                                            }}
                                        >
                                            <Mail className="h-3 w-3 text-foreground/50 shrink-0" />
                                            <span className="break-all">{m.value}</span>
                                            {m.undeliverableAt ? (
                                                <UndeliverableBadge
                                                    at={m.undeliverableAt}
                                                    reason={m.undeliverableReason}
                                                />
                                            ) : null}
                                            {m.memo || m.label ? (
                                                <span className="text-xs text-muted-foreground">
                                                    ({m.memo || m.label})
                                                </span>
                                            ) : null}
                                            {m.fromServiceTitan ? (
                                                <span
                                                    className="rounded border border-slate-300 bg-slate-100 px-1 text-[9px] text-slate-600 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-300"
                                                    title="Imported"
                                                >
                                                    IMP
                                                </span>
                                            ) : null}
                                            {added ? <span className="ml-auto text-[10px]">added</span> : null}
                                        </button>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>
            ) : null}

            {showOneTime ? (
                <div className="flex items-center gap-2">
                    <Input
                        type="email"
                        placeholder="name@example.com"
                        value={oneTimeValue}
                        onChange={(e) => setOneTimeValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                submitOneTime();
                            }
                        }}
                        className="h-8 text-sm"
                    />
                    <Button
                        type="button"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        disabled={!EMAIL_RE.test(oneTimeValue.trim())}
                        onClick={submitOneTime}
                    >
                        Add
                    </Button>
                </div>
            ) : null}

            {missingDocs.length > 0 ? (
                <div className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                    No recipient is set to receive: {missingDocs.map((d) => DOC_LABEL[d]).join(", ")}. Tick the document
                    on a recipient card (or uncheck it above) before sending.
                </div>
            ) : null}

            {onFileNotReceiving.length > 0 ? (
                <div className="rounded-md border border-sky-300/60 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200">
                    {onFileNotReceiving.length} contact{onFileNotReceiving.length === 1 ? "" : "s"} on file{" "}
                    {onFileNotReceiving.length === 1 ? "isn't" : "aren't"} receiving this send
                    {onFileNotReceiving.length <= 4 ? (
                        <> ({onFileNotReceiving.map((a) => a.contactName?.trim() || a.value).join(", ")})</>
                    ) : null}
                    . Add anyone who should via “Add saved contact” above.
                </div>
            ) : null}

            {effectivePlan.length > 0 ? (
                <p className="text-xs text-foreground/70 border-t pt-2">
                    {emailCount} email{emailCount === 1 ? "" : "s"} will be sent —{" "}
                    {sharedThreadCount > 1 ? (
                        <>
                            <span className="font-medium">1 shared thread</span> to{" "}
                            <span className="break-all">{ccShared.map((r) => r.value).join(", ")}</span> (
                            {docsSummary(sharedDocs)})
                            {ccSeparate.map((r) => (
                                <span key={r.value}>
                                    ; 1 to <span className="break-all font-medium">{r.value}</span> (
                                    {docsSummary(r.docs)})
                                </span>
                            ))}
                        </>
                    ) : (
                        effectivePlan.map((r, i) => (
                            <span key={r.value}>
                                {i > 0 ? "; " : ""}1 to <span className="break-all font-medium">{r.value}</span> (
                                {docsSummary(r.docs)})
                            </span>
                        ))
                    )}
                    .
                </p>
            ) : null}
        </div>
    );
}
